'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Is Your Coffee Cup Giving You Microplastics? The Truth About To-Go Cups"
      author="MicroPlastics Editorial Team"
      date="May 25, 2026"
      readTime="11 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics in coffee cups, the truth about to-go cups"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <p className="text-lg text-gray-700 leading-relaxed">
        Your paper coffee cup probably isn&apos;t just paper. Most to-go
        cups have a thin plastic lining so they can hold hot liquid
        without leaking, and heat is exactly what makes plastic shed
        more. If you grab a coffee on the way to work every morning,
        this may be one of the cheapest microplastic habits to fix, and the fix takes about 30 seconds.
      </p>

      <BlogTLDR>
        <p className="mb-3">
          <strong>Quick answer:</strong> Most disposable coffee cups
          have a polyethylene (PE) or PLA lining that holds hot liquid
          in, and a polypropylene or PS lid that sits over near-boiling
          coffee. Both shed microplastic particles into your drink. A
          2022 study estimated drinkers ingested ~1,500 microplastic
          particles per cup of hot drink served in a typical
          plastic-lined paper cup.
        </p>
        <p className="mb-3">
          <strong>Highest-risk situations:</strong> very hot beverages,
          long sip windows (commuting), acidic drinks (americano, cold
          brew with citrus), cracked or reused single-use cups, and
          drinks left in the cup for 30+ minutes with the lid sealed.
        </p>
        <p>
          <strong>Best first swap:</strong> a stainless steel or ceramic
          reusable mug, even a $5 thrift-store ceramic cup at the café
          works.
        </p>
      </BlogTLDR>

      <BlogComparisonTable
        caption="Coffee cup risk by format, quickest read"
        columns={[
          { key: 'cup', label: 'Cup format' },
          { key: 'material', label: 'Material' },
          { key: 'risk', label: 'Microplastic risk' },
          { key: 'verdict', label: 'Verdict' },
        ]}
        rows={[
          { cup: 'Standard paper to-go cup + plastic lid', material: 'Paperboard with PE / PLA liner + PP/PS lid', risk: 'High, ~1,500+ particles/cup (Liu 2022)', verdict: 'Avoid for daily use' },
          { cup: 'Double-walled hot cup (extra insulated)', material: 'Same PE liner + extra paperboard layer', risk: 'High, same liner', verdict: 'Marginal upgrade' },
          { cup: 'Polystyrene foam cup (#6)', material: 'Expanded polystyrene', risk: 'Very high, styrene migration with hot drink', verdict: 'Worst common option' },
          { cup: 'Café ceramic mug (drink in)', material: 'Glazed ceramic', risk: 'None', verdict: 'Best café option' },
          { cup: 'Personal stainless steel travel mug', material: '18/8 stainless', risk: 'None', verdict: 'Best portable option' },
          { cup: 'Personal glass coffee bottle', material: 'Borosilicate glass + silicone seal', risk: 'Near zero (silicone seal only)', verdict: 'Excellent for cold brew' },
        ]}
      />

      <BlogKeyTakeaways
        items={[
          <>Standard to-go &ldquo;paper&rdquo; cups are paperboard with a thin polyethylene or PLA plastic lining, that&apos;s what holds the liquid in.</>,
          <>The 2022 Liu et al. study estimated ~1,500 microplastic particles per cup released into hot drinks served in plastic-lined paper cups.</>,
          <>Heat is the dominant driver, the hotter the drink, the more particles release; sitting in the cup makes it worse.</>,
          <>Plastic lids (PP or PS) sit directly above the steam, more contact, more shed.</>,
          <>&ldquo;Compostable&rdquo; PLA-lined cups still release particles at brewing temperature, &ldquo;plant-based&rdquo; doesn&apos;t mean polymer-free.</>,
          <>The single best swap: a $20-30 stainless steel travel mug for the daily coffee habit.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why &ldquo;paper&rdquo; cups aren&apos;t really paper
      </h2>
      <p>
        Plain paper would turn into a soggy mess holding hot coffee for
        twenty minutes. Every disposable hot-drink cup needs a
        moisture-barrier liner to hold liquid in. The two common options
        are:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Polyethylene (PE) liner</strong>, the dominant US
          standard. A thin (~25-30 µm) plastic film bonded to the
          paperboard. Cheap, effective, recyclable in only a handful of
          specialized facilities.
        </li>
        <li>
          <strong>PLA (polylactic acid) liner</strong>, plant-based
          bioplastic, marketed as &ldquo;compostable&rdquo;. Better
          end-of-life story, but it&apos;s still a polymer that hydrolyzes
          and sheds at brewing temperature.
        </li>
      </ul>
      <p>
        The lid is a separate problem. Most takeaway lids are PP
        (polypropylene, recycling #5) or PS (polystyrene, #6) and sit
        directly above the steam from a fresh cup. PS in particular
        leaches styrene into hot beverages.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What the 2022 hot-drink cup study found
      </h2>
      <p>
        Liu et al. (2022, <em>Hazardous Materials Advances</em>)
        examined plastic-lined paper cups filled with 85°C (185°F)
        water (the typical serving temperature for coffee or tea) and
        measured released particles using both light microscopy and
        electron microscopy.
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Estimated <strong>~1,500 microplastic particles per cup</strong> released into the hot drink.</li>
        <li>Particle release scaled with temperature and contact time.</li>
        <li>Multiple polymer types detected in the liquid, matching the cup liner composition.</li>
        <li>Other studies (e.g., Ranjan 2021) have reported similar or higher counts for some cup types.</li>
      </ul>
      <p>
        For context: if you buy two coffees a day in plastic-lined cups,
        that&apos;s an estimated 1.1 million extra microplastic
        particles per year, from a single daily habit.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Brand-by-brand reality check
      </h2>
      <BlogComparisonTable
        caption="Common coffee chains, cup format (verify current product line)"
        columns={[
          { key: 'chain', label: 'Chain' },
          { key: 'standard', label: 'Standard hot cup' },
          { key: 'lid', label: 'Standard lid' },
          { key: 'note', label: 'Note' },
        ]}
        rows={[
          { chain: 'Starbucks', standard: 'Paperboard with PE liner', lid: 'PP plastic (#5) or new fiber-based', note: 'Free reusable program; bring a ceramic mug for-here' },
          { chain: 'Dunkin&apos;', standard: 'Paperboard with PE liner', lid: 'PP plastic (#5)', note: 'Switched away from foam in 2020; bring your own' },
          { chain: 'McDonald&apos;s', standard: 'Paperboard with PE liner', lid: 'PP plastic (#5)', note: 'Reusable cup discount in some markets' },
          { chain: 'Tim Hortons', standard: 'Paperboard with PE liner', lid: 'PP plastic (#5)', note: 'Some markets piloting reusable schemes' },
          { chain: 'Pret a Manger', standard: 'Paperboard with PE liner', lid: 'PP / fiber blend', note: 'UK discount for personal cup' },
          { chain: 'Local independent café', standard: 'Variable, verify', lid: 'Variable', note: 'Most accept clean personal mug; many offer discount' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        When to-go is unavoidable: lower-risk habits
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Ask for no lid</strong> if you&apos;ll drink quickly, eliminates the lid-side exposure.</li>
        <li><strong>Pour into your own travel mug</strong> at the café, they&apos;ll fill it directly if you ask.</li>
        <li><strong>Skip the lid stir-stick.</strong> Plastic stirrer + hot liquid + acid = bad combo.</li>
        <li><strong>Drink it within 10-15 minutes.</strong> Longer contact = more particles + more lid-side leaching.</li>
        <li><strong>Don&apos;t reuse a single-use cup.</strong> A reheated paper-with-plastic-liner cup is the worst option in your kitchen.</li>
        <li><strong>Avoid PS / foam cups.</strong> Some convenience stores and budget cafés still use them.</li>
      </ol>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Best reusable travel mug picks
      </h2>
      <BlogComparisonTable
        caption="Best reusable coffee mugs for 2026"
        columns={[
          { key: 'pick', label: 'Pick' },
          { key: 'material', label: 'Material' },
          { key: 'price', label: 'Price' },
          { key: 'note', label: 'Note' },
        ]}
        rows={[
          { pick: 'Klean Kanteen Insulated TKWide 16oz', material: '18/8 stainless + silicone lid seal', price: '$30-40', note: 'Best all-around; ceramic-lined interior available' },
          { pick: 'Yeti Rambler 14oz Mug', material: 'Stainless + ceramic interior option', price: '$30-40', note: 'Excellent heat retention; common in cafés' },
          { pick: 'Stanley The Quencher H2.0', material: 'Stainless + plastic lid', price: '$35-45', note: 'Cult favorite; lid is plastic so drink fast or remove lid' },
          { pick: 'Miir Camp Cup 12oz', material: '18/8 stainless', price: '$28-35', note: 'Café-friendly form; ceramic-lined available' },
          { pick: 'KeepCup Brew (glass)', material: 'Tempered glass + silicone band + cork', price: '$28-35', note: 'Glass cup that fits standard espresso bars' },
          { pick: 'JOCO Cup', material: 'Borosilicate glass + silicone band', price: '$25-35', note: 'No plastic in the drink path' },
          { pick: 'Hydro Flask Coffee 12oz/16oz', material: '18/8 stainless', price: '$35-45', note: 'Strong insulation; flip lid is plastic' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        What about home coffee setups?
      </h2>
      <p>
        The to-go cup is one daily exposure. Home brewing has its own:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>K-cups / pod coffee</strong>, plastic capsules in contact with hot water; switch to stainless reusable pods or skip the format.</li>
        <li><strong>Plastic French press</strong>, uncommon but exists; glass or stainless preferred.</li>
        <li><strong>Plastic electric kettles</strong>, see our <Link href="/blog/microplastics-in-electric-kettles" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">electric kettles guide</Link>; glass or fully stainless models eliminate the kettle-side exposure.</li>
        <li><strong>Plastic drip baskets / carafes</strong>, older machines especially.</li>
      </ul>

      <BlogWhatTheAppChecks
        items={[
          <>Cup or lid material from the photo or barcode, paperboard with PE liner, PLA, PP lid, PS lid.</>,
          <>Brand and product line, coffee chain, K-cup pod, packaged cold brew.</>,
          <>Use-context flags you log, temperature, sit time, lid on/off.</>,
          <>Container condition, for reusable mugs (scratched lid seals, worn ceramic interior).</>,
          <>Linked published studies behind the 0–100 risk score, including Liu 2022.</>,
        ]}
      />

      <BlogAppCTA
        headline="Scan your bottled drinks, coffee products, and hot-drink packaging"
        sub="Snap the cup, the lid, the K-cup, the cold-brew bottle. The MicroPlastics app weighs material + temperature + brand and gives a 0–100 risk score with a safer swap."
        buttonLabel="Scan coffee products in the app"
      />

      <p>
        Related reading:{' '}
        <Link href="/blog/microplastics-in-coffee" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in coffee
        </Link>,{' '}
        <Link href="/blog/microplastics-in-coffee-by-brewing-method" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          coffee by brewing method
        </Link>,{' '}
        <Link href="/blog/microplastics-in-electric-kettles" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          electric kettles
        </Link>,{' '}
        <Link href="/blog/best-plastic-free-food-storage" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          30 kitchen swaps
        </Link>,{' '}
        <Link href="/blog/check-products-for-microplastics-before-buying" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          check before you buy
        </Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Do paper coffee cups have plastic in them?',
            answer:
              'Almost always yes. Standard to-go paper cups have a thin polyethylene (PE) plastic film bonded to the paperboard to hold liquid in. "Compostable" versions use PLA, which is still a polymer that releases particles at brewing temperature. Without a plastic or polymer liner, paper would turn to mush within minutes of holding hot liquid.',
          },
          {
            question: 'How many microplastics does a coffee cup release?',
            answer:
              'A 2022 study by Liu et al. estimated approximately 1,500 microplastic particles per cup released from plastic-lined paper cups holding hot drinks at 85°C (typical serving temperature). Particle release scales with temperature, contact time, and the specific liner material.',
          },
          {
            question: 'Are Starbucks cups plastic-lined?',
            answer:
              'Yes. Standard Starbucks hot cups are paperboard with a polyethylene (PE) liner, and the standard hot lid is polypropylene (PP, recycling #5). Starbucks offers reusable cup discounts in many markets and accepts personal mugs. For-here orders can be served in ceramic mugs at most locations, just ask.',
          },
          {
            question: 'Are compostable PLA-lined coffee cups safer?',
            answer:
              'Slightly, environmentally, but not meaningfully for microplastic release into the drink. PLA is a bioplastic polymer that hydrolyzes and releases particles at brewing temperature similar to PE. The end-of-life story is better (industrial composting), but the in-cup exposure is roughly comparable.',
          },
          {
            question: 'What\'s the safest reusable coffee cup?',
            answer:
              'Stainless steel travel mugs (Klean Kanteen Insulated TKWide, Yeti Rambler, Miir Camp Cup), ceramic-lined stainless mugs, or glass mugs (KeepCup Brew, JOCO Cup) all eliminate the plastic-in-drink-path issue. Look for an 18/8 stainless interior and a silicone lid seal rather than a plastic one if you want maximum reduction.',
          },
          {
            question: 'Does the lid matter as much as the cup?',
            answer:
              'It can. The plastic lid sits directly above the steam from hot coffee and contacts the drink each time you sip. PP lids (most common) are reasonably stable; PS lids (some budget café and convenience-store cups) leach styrene more readily with hot drinks. Asking "no lid" or "in a for-here mug" eliminates this exposure.',
          },
          {
            question: 'Are K-cups safe?',
            answer:
              'K-cups expose your coffee to a heated plastic capsule under high pressure. The exposure per cup is meaningful, especially with daily use. Reusable stainless steel K-cup pods are widely available for $5-15 and let you use any ground coffee. The same goes for Nespresso reusable stainless pods.',
          },
          {
            question: 'Should I stop buying café coffee?',
            answer:
              'No: bring your own mug. Most chains and independent cafés will fill a personal travel mug or serve "for-here" in a ceramic mug. Many offer a discount (10-25 cents) for bringing your own cup. The fix is the cup, not the coffee.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Liu G, Wang J, Wang M, et al.',
            year: 2022,
            title: 'Disposable plastic materials release microplastics and harmful substances in hot water',
            publisher: 'Science of the Total Environment',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0048969721075057',
          },
          {
            authors: 'Ranjan VP, Joseph A, Goel S',
            year: 2021,
            title: 'Microplastics and other harmful substances released from disposable paper cups into hot water',
            publisher: 'Journal of Hazardous Materials',
            url: 'https://doi.org/10.1016/j.jhazmat.2020.124118',
          },
          {
            authors: 'Hernandez LM, Xu EG, Larsson HCE, et al.',
            year: 2019,
            title: 'Plastic Teabags Release Billions of Microparticles and Nanoparticles into Tea',
            publisher: 'Environmental Science & Technology',
            url: 'https://pubs.acs.org/doi/10.1021/acs.est.9b02540',
          },
          {
            authors: 'WHO',
            year: 2022,
            title: 'Dietary and inhalation exposure to nano- and microplastic particles',
            publisher: 'World Health Organization',
            url: 'https://www.who.int/publications/i/item/9789240054608',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/4501',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
